<template>
  <div class="ips-editor">
    <header class="page-header">
      <div>
        <button class="btn btn-link" @click="$router.back()">
          <i class="fas fa-arrow-left"></i> Back
        </button>
        <h1 v-if="household">IPS: {{ household.name }}</h1>
      </div>
    </header>

    <div v-if="loading" class="loading-state">Loading IPS…</div>

    <form v-else class="ips-form" @submit.prevent="save">
      <section class="form-section">
        <h2>Risk Profile</h2>
        <div class="form-grid">
          <label>
            Risk Tolerance
            <select v-model="form.risk_tolerance">
              <option value="CONSERVATIVE">Conservative</option>
              <option value="MODERATE_CONS">Moderately Conservative</option>
              <option value="MODERATE">Moderate</option>
              <option value="MODERATE_AGG">Moderately Aggressive</option>
              <option value="AGGRESSIVE">Aggressive</option>
            </select>
          </label>
          <label>
            Risk Score (1-10)
            <input
              v-model.number="form.risk_score"
              type="number"
              min="1"
              max="10"
            />
          </label>
          <label>
            Time Horizon (years)
            <input v-model.number="form.time_horizon_years" type="number" min="0" max="50" />
          </label>
          <label>
            Liquidity Needs (%)
            <input v-model.number="form.liquidity_needs_pct" type="number" step="0.1" min="0" />
          </label>
        </div>
      </section>

      <section class="form-section">
        <h2>Target Allocation</h2>
        <p class="help">
          Weights must sum to ~1.0 (100%). Current sum:
          <strong :class="{ error: !allocationValid }">
            {{ (allocationSum * 100).toFixed(1) }}%
          </strong>
        </p>
        <div class="alloc-grid">
          <label v-for="cls in assetClasses" :key="cls">
            {{ cls.replace('_', ' ') }}
            <input
              :value="(form.target_allocation[cls] ?? 0) * 100"
              type="number"
              step="0.5"
              min="0"
              max="100"
              @input="setAlloc(cls, $event.target.value)"
            />
            %
          </label>
        </div>
      </section>

      <section class="form-section">
        <h2>Thresholds</h2>
        <div class="form-grid">
          <label>
            Drift Threshold (%)
            <input v-model.number="form.drift_threshold_pct" type="number" step="0.1" min="0" />
          </label>
          <label>
            Concentration Threshold (%)
            <input v-model.number="form.concentration_threshold_pct" type="number" step="0.1" min="0" />
          </label>
          <label>
            Cash Min (%)
            <input v-model.number="form.cash_target_min_pct" type="number" step="0.1" min="0" />
          </label>
          <label>
            Cash Max (%)
            <input v-model.number="form.cash_target_max_pct" type="number" step="0.1" min="0" />
          </label>
        </div>
      </section>

      <section class="form-section">
        <h2>Constraints</h2>
        <label>
          Prohibited Symbols (comma separated)
          <input
            v-model="prohibitedText"
            type="text"
            placeholder="e.g. TSLA, MO, NVDA"
          />
        </label>
        <label>
          Required Symbols (comma separated)
          <input
            v-model="requiredText"
            type="text"
            placeholder="e.g. SPY, AGG"
          />
        </label>
        <label>
          Notes
          <textarea v-model="form.notes" rows="4"></textarea>
        </label>
      </section>

      <footer class="form-footer">
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="saving || !allocationValid"
        >
          <i class="fas fa-save"></i>
          {{ saving ? 'Saving…' : 'Save IPS' }}
        </button>
        <span v-if="lastSavedAt" class="saved-indicator">
          Last saved: {{ formatDate(lastSavedAt) }}
        </span>
      </footer>
    </form>
  </div>
</template>

<script>
import api from '@/services/api'

const ASSET_CLASSES = ['US_EQUITY', 'INTL_EQUITY', 'FIXED_INCOME', 'CASH', 'ALT']

export default {
  name: 'IPSEditor',

  data() {
    return {
      household: null,
      form: this.emptyForm(),
      loading: false,
      saving: false,
      lastSavedAt: null,
      prohibitedText: '',
      requiredText: '',
      assetClasses: ASSET_CLASSES,
    }
  },

  computed: {
    allocationSum() {
      return Object.values(this.form.target_allocation || {})
        .reduce((acc, v) => acc + Number(v || 0), 0)
    },
    allocationValid() {
      return Math.abs(this.allocationSum - 1.0) <= 0.02
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    emptyForm() {
      return {
        risk_tolerance: 'MODERATE',
        risk_score: 5,
        target_allocation: ASSET_CLASSES.reduce(
          (acc, cls) => ({ ...acc, [cls]: 0 }), {},
        ),
        drift_threshold_pct: 5.0,
        concentration_threshold_pct: 10.0,
        cash_target_min_pct: 1.0,
        cash_target_max_pct: 5.0,
        prohibited_symbols: [],
        required_symbols: [],
        time_horizon_years: 10,
        liquidity_needs_pct: 0.0,
        notes: '',
      }
    },
    async load() {
      this.loading = true
      const id = this.$route.params.householdId
      try {
        const { data: h } = await api.get(`/wealth/households/${id}/`)
        this.household = h
        try {
          const { data: ips } = await api.get(`/wealth/households/${id}/ips/`)
          this.applyIps(ips)
        } catch (e) {
          if (e.response?.status !== 404) throw e
        }
      } finally {
        this.loading = false
      }
    },
    applyIps(ips) {
      this.form = {
        risk_tolerance: ips.risk_tolerance || 'MODERATE',
        risk_score: ips.risk_score || 5,
        target_allocation: { ...this.emptyForm().target_allocation, ...(ips.target_allocation || {}) },
        drift_threshold_pct: Number(ips.drift_threshold_pct) || 5.0,
        concentration_threshold_pct: Number(ips.concentration_threshold_pct) || 10.0,
        cash_target_min_pct: Number(ips.cash_target_min_pct) || 1.0,
        cash_target_max_pct: Number(ips.cash_target_max_pct) || 5.0,
        prohibited_symbols: ips.prohibited_symbols || [],
        required_symbols: ips.required_symbols || [],
        time_horizon_years: ips.time_horizon_years || 10,
        liquidity_needs_pct: Number(ips.liquidity_needs_pct) || 0.0,
        notes: ips.notes || '',
      }
      this.prohibitedText = (ips.prohibited_symbols || []).join(', ')
      this.requiredText = (ips.required_symbols || []).join(', ')
      this.lastSavedAt = ips.last_reviewed_at
    },
    setAlloc(cls, pctString) {
      const pct = Number(pctString || 0) / 100
      this.form.target_allocation = {
        ...this.form.target_allocation,
        [cls]: pct,
      }
    },
    async save() {
      this.saving = true
      const id = this.$route.params.householdId
      try {
        const payload = {
          ...this.form,
          prohibited_symbols: this.prohibitedText
            .split(',')
            .map(s => s.trim().toUpperCase())
            .filter(Boolean),
          required_symbols: this.requiredText
            .split(',')
            .map(s => s.trim().toUpperCase())
            .filter(Boolean),
        }
        const { data } = await api.put(`/wealth/households/${id}/ips/`, payload)
        this.applyIps(data)
        this.lastSavedAt = new Date().toISOString()
      } catch (err) {
        alert('Save failed: ' + (err.response?.data?.detail || err.message))
      } finally {
        this.saving = false
      }
    },
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleString()
    },
  },
}
</script>

<style scoped>
.ips-editor {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}
.page-header { margin-bottom: 20px; }
.page-header h1 { color: #0f2b4a; margin: 8px 0 0 0; }
.ips-form { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; }
.form-section { margin-bottom: 24px; }
.form-section h2 {
  color: #0f2b4a;
  font-size: 16px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 6px;
}
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.alloc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}
label {
  display: block;
  font-size: 12px;
  color: #334155;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
input[type="text"], input[type="number"], select, textarea {
  width: 100%;
  padding: 6px 10px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 5px;
  margin-top: 4px;
  background: #fff;
}
textarea { resize: vertical; }
.help { color: #64748b; font-size: 13px; }
.help .error { color: #b91c1c; }
.form-footer {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
.saved-indicator { color: #64748b; font-size: 12px; }
.btn {
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary { background: #0f2b4a; color: #fff; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-link { background: transparent; color: #0f2b4a; }
.loading-state { text-align: center; padding: 40px; color: #64748b; }
</style>
